import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import Card from '@/components/ui/Card'
import { ChevronRight, ChevronLeft } from 'lucide-react'
import { cn } from '@/utils/cn'

interface CalendarDay {
  date: Date
  prayers: {
    name: string // i18n key
    time: string
  }[]
}

interface PrayerCalendarProps {
  days: CalendarDay[]
}

export default function PrayerCalendar({ days }: PrayerCalendarProps) {
  const { t, i18n } = useTranslation()
  const [view, setView] = useState<'today' | 'tomorrow' | 'month'>('today')
  const [monthOffset, setMonthOffset] = useState(0)

  const formatDate = (date: Date) => {
    const day = date.getDate()
    const month = date.toLocaleDateString(i18n.language, { month: 'short' })
    return `${day} ${month}`
  }

  const formatHijriDate = (date: Date) => {
    const hijriMonths = ['محرم', 'صفر', 'ربيع الأول']
    return `${date.getDate()} ${hijriMonths[date.getMonth() % 3]}`
  }

  const isSameDay = (d1: Date, d2: Date) =>
    d1.getFullYear() === d2.getFullYear() &&
    d1.getMonth() === d2.getMonth() &&
    d1.getDate() === d2.getDate()

  const today = new Date()

  const startIndex = days.findIndex((d) => isSameDay(d.date, today))
  const baseIndex = startIndex === -1 ? 0 : startIndex

  const count = view === 'month' ? 30 : view === 'tomorrow' ? 2 : 1
  const displayedDays = days.length > 0
    ? days.slice(baseIndex, baseIndex + count)
    : []

  return (
    <Card>
      <div className="flex items-center justify-between mb-6">
        <h3 className="text-xl font-bold text-gray-900 dark:text-gray-100">{t('prayer.calendar')}</h3>
        <div className="flex gap-2">
          <button
            onClick={() => setView('today')}
            className={cn(
              'px-4 py-2 rounded-lg text-sm font-medium transition-colors',
              view === 'today'
                ? 'bg-primary-600 dark:bg-primary-500 text-white'
                : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600'
            )}
          >
            {t('prayer.today')}
          </button>
          <button
            onClick={() => setView('tomorrow')}
            className={cn(
              'px-4 py-2 rounded-lg text-sm font-medium transition-colors',
              view === 'tomorrow'
                ? 'bg-primary-600 dark:bg-primary-500 text-white'
                : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600'
            )}
          >
            {t('prayer.tomorrow')}
          </button>
          <button
            onClick={() => setView('month')}
            className={cn(
              'px-4 py-2 rounded-lg text-sm font-medium transition-colors',
              view === 'month'
                ? 'bg-primary-600 dark:bg-primary-500 text-white'
                : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600'
            )}
          >
            {t('prayer.days30')}
          </button>
        </div>
      </div>

      {view === 'month' && (
        <div className="flex items-center justify-between mb-4">
          <button
            onClick={() => setMonthOffset(monthOffset - 1)}
            className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
            aria-label="Previous month"
          >
            <ChevronRight className="w-5 h-5 text-gray-700 dark:text-gray-300" />
          </button>
          <span className="font-semibold text-gray-900 dark:text-gray-100">
            {new Date(
              new Date().getFullYear(),
              new Date().getMonth() + monthOffset,
              1
            ).toLocaleDateString(i18n.language, {
              month: 'long',
              year: 'numeric',
            })}
          </span>
          <button
            onClick={() => setMonthOffset(monthOffset + 1)}
            className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
            aria-label="Next month"
          >
            <ChevronLeft className="w-5 h-5 text-gray-700 dark:text-gray-300" />
          </button>
        </div>
      )}

      <div className="space-y-4 max-h-96 overflow-y-auto">
        {displayedDays.map((day, index) => (
          <div
            key={index}
            className="border-b border-gray-100 dark:border-gray-700 pb-4 last:border-0 last:pb-0"
          >
            <div className="flex items-center justify-between mb-3">
              <div>
                <p className="font-semibold text-gray-900 dark:text-gray-100">
                  {formatDate(day.date)}
                </p>
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  {formatHijriDate(day.date)}
                </p>
              </div>
            </div>
            <div className="grid grid-cols-5 gap-2 text-sm">
              {day.prayers.map((prayer, pIndex) => (
                <div key={pIndex} className="text-center">
                  <p className="text-xs text-gray-500 dark:text-gray-400 mb-1">
                    {t(`prayer.prayerNames.${prayer.name}`)}
                  </p>
                  <p className="font-mono font-medium text-gray-900 dark:text-gray-100">
                    {prayer.time}
                  </p>
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>
    </Card>
  )
}
